import React, { useContext, useEffect, useState } from 'react'
import { useFirebase } from 'react-redux-firebase'
import { Container, Row, Col, Spinner, Card, CardBody, CardTitle, Button } from 'reactstrap'
import { Divider, Typography } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { Prompt } from 'react-router-dom'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import { paymentErrorKey } from '../components/PaymentErrors'
import { AppconfigContext } from '../appconfig-context'
import { directPaymentEnabled, directProvider, DIRECT_GATEWAYS } from '../utils/directPayment'


const buttonStyle = {
  backgroundColor: '#FFFFFF',
  border: 0,
  borderRadius: '0.5rem',
  boxSizing: 'border-box',
  color: '#111827',
  // fontFamily: "Inter var",ui-sans-serif,system-ui,-apple-system,system-ui,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji",
  fontSize: '.875rem',
  fontWeight: 600,
  lineHeight: '1.25rem',
  padding: '0.75rem 1rem',
  textAlign: 'center',
  textDecoration: 'none #D1D5DB solid',
  textDecorationThickness: 'auto',
  boxShadow: '0 1px 3px 0 rgb(0 0 0 / 10%), 0 1px 2px 0 rgb(0 0 0 / 6%)',
  cursor: 'pointer',
  userSelect: 'none',
  touchAction: 'manipulation',
  width: 100,
  height: 50,
  margin: 5,
}


const isInIframe = () => {
  try {
    return window.self !== window.top
  } catch (e) {
    return true
  }
}

function PaymentOptions({ selectedGw, selectedGroup, provider, disabledmethods, showConfirmation, confirmationtype, onCancel, cancellable, forwardToPaytrail }) {
  const firebase = useFirebase()
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [paymentOptions, setPaymentOptions] = useState([])
  const [undoClicked, setUndoClicked] = useState(false)
  const [forwarded, setForwarded] = useState(false)
  const isTest = document.location.hostname === 'utest.cintoia.com' || document.location.hostname === 'localhost'
  // Paying with a stored card: init answers with an href to follow - the success page when the charge
  // went through, or the bank's 3D Secure page when it needs the customer - never a provider list.
  const isCardPayment = Boolean(provider && provider.gateway === 'card')
  // experimental.directpayment: the method was chosen on the checkout, which also showed the terms and
  // the 10-minute notice, so the chosen provider's form is submitted at once (utils/directPayment.js).
  // Not in an iframe - Paytrail opens in a new window there, which needs the customer's click.
  const appconfig = useContext(AppconfigContext)
  const gateway = provider ? provider.gateway : selectedGw
  const direct =
    directPaymentEnabled(appconfig, window.location) &&
    DIRECT_GATEWAYS.includes(gateway) &&
    !isCardPayment &&
    !forwardToPaytrail &&
    !isInIframe()
  const [directSubmitted, setDirectSubmitted] = useState(false)
  const url =
    'https://europe-west1-falcon-328a1.cloudfunctions.net/' +
    (isTest ? 'testpaymentv3' : 'paymentv3') +
    '-init/?p=web&pm='

  useEffect(() => {
    firebase
      .auth()
      .currentUser.getIdTokenResult()
      .then((tokenResult) => {
        // A stored card is charged directly (card=<cardid>); every other provider is offered by id (me=).
        const which = isCardPayment ? '&card=' + encodeURIComponent(provider.id) : '&me=' + provider.id
        return fetch(url + (provider ? provider.gateway : selectedGw) + '&auth=' + tokenResult.token + '&f=j' + which)
      })
      .then((response) => response.json())
      .then((data) => {
        // A card payment that comes back with neither an error nor somewhere to go would otherwise
        // send the browser to "undefined" - show it as a refused payment instead.
        if (isCardPayment && data && !data.error && !data.href) {
          setPaymentOptions({ error: true, message: 'card_payment_unavailable' })
        } else {
          setPaymentOptions(data)
        }
        setLoading(false)
      })
      .catch((e) => {
        console.error(e)
        // An answer that is not JSON (a backend that does not know card payments, or one that failed) would
        // spin forever: show it as a refused payment, with the way back, for every provider.
        setPaymentOptions({ error: true, message: isCardPayment ? 'card_payment_unavailable' : 'payment_unavailable' })
        setLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  const checkoutParameterToInput = (param) => (
    <input key={param.name} type='hidden' name={param.name} value={param.value} />
  )

  const handleSubmit = (e) => {
    if (e) {
      const form = e.target
      form.submit()
      e.preventDefault()
    } else {
      const form = document.getElementById('payment-form')
      form.submit()
    }
    // e.preventDefault()
    setLoading(true)
  }      


  // The providers this page offers: the group asked for, without the venue's disabled methods.
  const visibleProviders = (data) =>
    data && Array.isArray(data.providers)
      ? data.providers
          .filter((x) => !selectedGroup || (selectedGroup && x.group === selectedGroup))
          .filter((x) => !disabledmethods.includes(x.id))
      : []

  const directTarget = direct && !loading && paymentOptions && !paymentOptions.error ? directProvider(visibleProviders(paymentOptions)) : null

  // Submits the chosen provider's form, built from its own parameters (every form on this page shares
  // id 'payment-form', so the DOM one is not reliable when there are several).
  useEffect(() => {
    if (!directTarget || directSubmitted) return
    setDirectSubmitted(true)
    const form = document.createElement('form')
    form.method = directTarget.method && directTarget.method === 'get' ? 'GET' : 'POST'
    form.action = directTarget.url
    form.target = '_self'
    directTarget.parameters.forEach((p) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = p.name
      input.value = p.value
      form.appendChild(input)
    })
    document.body.appendChild(form)
    form.submit()
  }, [directTarget, directSubmitted])

  const checkoutResponseToHtml = (data) => {
    if (!data) return null
    if (!data.providers) return null
    if (data.providers.length === 0) return null
    return visibleProviders(data)
      .map((provider) => {
        const params = provider.parameters.map((x) => checkoutParameterToInput(x))
        const _isInIframe = isInIframe()
        const _target = _isInIframe ? '_blank' : '_self'
        const _method = provider.method && provider.method === 'get' ? 'GET' : 'POST'
        return (
          <Col key={provider.name + '-col'}>
            {loading ? (
              <Row className={'loose h-100 justify-content-center align-items-center'} key={'poptions'}>
                <Spinner />
              </Row>
            ) :
              (<button style={buttonStyle} key={provider.name + '-button'} onClick={() => {
                handleSubmit()
              }}>
                <img src={provider.svg} alt={provider.name} style={{ height: '100%', width: '100%' }} />
              </button>
              )}
            <form key={provider.name} method={_method} action={provider.url} target={_target} id='payment-form'>
              {params}
              {/* <button style={buttonStyle}>
                <img src={provider.svg} alt={provider.name} style={{ height: '100%', width: '100%' }} />
              </button> */}
            </form>
          </Col>
        )
      })
  }

  const confirmation = () => {
    if (confirmationtype === 'reservation') {
      return (
        <React.Fragment>
          <Row key={'1'} className='justify-content-center align-items-center'>
            <Col xs='12'>
              <Typography variant='h5' component='h5' style={{ textAlign: 'center' }}>
                {t('reservation_confirmation_title')}
              </Typography>
            </Col>
          </Row>
          <Row key={'2'} className='justify-content-center align-items-center'>
            <Col xs='12'>
              <Typography variant='body1' style={{ textAlign: 'center' }}>
                {t('reservation_confirmation_message')}
              </Typography>
            </Col>
          </Row>
        </React.Fragment>
      )
    }
  }

  const onUndo = (id, locationid) => {
    setUndoClicked(true)
    onCancel(id, locationid)
  }

  const undoButton = (label) =>
    undoClicked ? (
      <React.Fragment><ChevronLeftIcon className='text-muted' />{' '}<Spinner size={'sm'} className='text-muted' /></React.Fragment>
    ) : (
      <Button size='small' color='link' className='text-muted' onClick={() => onUndo(cancellable.id, cancellable.locationid)}>
        <ChevronLeftIcon /> {label || t('undo_payment')}
      </Button>
    )

  // A refused payment (paymentv3-init answers { error, message: <reason code> }): say why in words,
  // keep the code small for customer service, and offer the way out -- cancel and pick another method.
  if (paymentOptions && paymentOptions.error) {
    const canUndo = Boolean(onCancel && cancellable)
    // Shop purchases name their type (membership, eventtickets, ...) and have nothing tentative to
    // cancel - nothing was bought, so the way out is back. A court booking (no type) does hold a
    // tentative reservation, so it keeps the cancel wording.
    const isPurchase = Boolean(cancellable && cancellable.type)
    return (
      <div className='full-page-background'>
        <Container className='login-dialog'>
          <Row>
            <Col md='8' className='mx-auto'>
              <Card>
                <CardBody>
                  <CardTitle tag='h5'>{t('payment_refused_title')}</CardTitle>
                  <Typography variant='body1' style={{ marginBottom: 12 }}>
                    {t(paymentErrorKey(paymentOptions.message))}
                  </Typography>
                  {canUndo ? (
                    <Typography variant='body2'>{t(isPurchase ? 'payment_refused_hint_purchase' : 'payment_refused_hint')}</Typography>
                  ) : null}
                  {paymentOptions.message ? (
                    <Typography variant='caption' className='text-muted' component='div' style={{ marginTop: 12 }}>
                      {t('payment_refused_code')}: {String(paymentOptions.message)}
                    </Typography>
                  ) : null}
                </CardBody>
              </Card>
            </Col>
          </Row>
          {canUndo ? (
            <Row>
              <Col md='8' className='mx-auto'>
                <Card>
                  <CardBody>{undoButton(isPurchase ? t('back') : null)}</CardBody>
                </Card>
              </Col>
            </Row>
          ) : null}
        </Container>
      </div>
    )
  }


  // Going straight to Paytrail: only a short wait on our side.
  if (directTarget) {
    return (
      <div className='full-page-background'>
        <Container className='login-dialog'>
          <Row>
            <Col md='8' className='mx-auto'>
              <Card>
                <CardBody className='text-center'>
                  <Spinner />
                  <Typography variant='body1' style={{ marginTop: 12 }}>
                    {t('payment_redirecting')}
                  </Typography>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    )
  }

  if (forwardToPaytrail || isCardPayment) {
    if (loading) {
      return (
        <div className='full-page-background'>
          <Container className='login-dialog'>
            <Row>
              <Col md='8' className='mx-auto'>
                <Card>
                  <CardBody>
                    <CardTitle tag='h5'>{'Maksutapa'}</CardTitle>
                      <Row className={'loose h-100 justify-content-center align-items-center'} key={'poptions'}>
                      <Spinner />
                    </Row>
                </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      )
    } else {
      if(isInIframe()) {
        // window.location.href = paymentOptions.href
        if(!forwarded) {
          setForwarded(true)
          window.open(paymentOptions.href, '_blank')
        }
      } else {
        window.location.href = paymentOptions.href
      }
      return null
    }
  }

  return (
    <div className='full-page-background'>
      <Container className='login-dialog'>
        {showConfirmation ? (
          <Row>
            <Col md='8' className='mx-auto'>
              <Card>
                <CardBody>{confirmation()}</CardBody>
              </Card>
            </Col>
          </Row>
        ) : null}
        <Row>
          <Col md='8' className='mx-auto'>
            <Card>
              <CardBody>
                <CardTitle tag='h5'>{'Maksutapa'}</CardTitle>
                  <React.Fragment>
                    <Row className={'loose'} key={'poptions'}>
                      {checkoutResponseToHtml(paymentOptions)}
                    </Row>
                    <Divider />
                    <Row className={'loose'} key={'day'}>
                      <Col
                        style={{ fontSize: 'small' }}
                        dangerouslySetInnerHTML={{ __html: paymentOptions.terms }}
                      ></Col>
                    </Row>
                  </React.Fragment>
                {/* )} */}
              </CardBody>
            </Card>
          </Col>
        </Row>
        {onCancel && cancellable && !loading ? (
          <Row>
            <Col md='8' className='mx-auto'>
              <Card>
                <CardBody>
                  {undoButton(cancellable.type === 'pastpayment' ? t('back') : null)}
                </CardBody>
              </Card>
            </Col>
          </Row>
        ) : null}
      </Container>
      <Prompt
        when={showConfirmation && !undoClicked}
        message={(location, action) => {
          return t('reservation_back_prompt')
        }}
      />
    </div>
  )
}

export default  PaymentOptions
